/**
 * Growth Marketing story pages. Routes, legacy bookmarks, and the
 * numbers the pages compare. The numbers stay here so the pages can
 * change tone without a code hunt. Nothing in this file is shown as a
 * code, a flag, or a percent on the page.
 */

export const GROWTH_SECTION_LABEL = "Growth Marketing";

export const GROWTH_CALLS_DONE_SHARE = 0.5;
export const GROWTH_REPLY_WAIT_HOURS = 48;
export const GROWTH_WEEK_DROP_SHARE = 0.25;
export const GROWTH_PAGE_DROP_COUNT = 5;

export type GrowthPage = {
  href: string;
  label: string;
  legacy: readonly string[];
};

export const GROWTH_PAGES = [
  {
    href: "/admin/who-we-could-sell-to",
    label: "Who we could sell to",
    legacy: ["/admin/tam"],
  },
  {
    href: "/admin/who-to-call",
    label: "Who to call this week",
    legacy: ["/admin/weekly-pull-list"],
  },
  {
    href: "/admin/conversations",
    label: "Conversations",
    legacy: ["/admin/outreach-list"],
  },
  {
    href: "/admin/this-week",
    label: "This Week",
    legacy: ["/admin/weekly-scorecard", "/admin/weekly-review"],
  },
  {
    href: "/admin/can-people-find-us",
    label: "Can people find us on Google",
    legacy: ["/admin/index-coverage"],
  },
] as const satisfies readonly GrowthPage[];

export type GrowthHref = (typeof GROWTH_PAGES)[number]["href"];

const LEGACY_TO_HREF = new Map<string, GrowthHref>();
for (const page of GROWTH_PAGES) {
  for (const old of page.legacy) LEGACY_TO_HREF.set(old, page.href);
}

export function growthHrefForLegacy(path: string): GrowthHref | null {
  return LEGACY_TO_HREF.get(path) ?? null;
}

/**
 * A person who already has an older menu grant still sees the story
 * page. This does not add a grant. Core admins already see every item.
 */
export function growthHrefAllowed(href: string, allowed: ReadonlySet<string>): boolean {
  if (allowed.has(href)) return true;
  const page = GROWTH_PAGES.find((item) => item.href === href);
  if (!page) return false;
  return page.legacy.some((old) => allowed.has(old));
}

export function withSearch(path: string, search: string): string {
  const query = search.replace(/^\?/, "").trim();
  return query ? `${path}?${query}` : path;
}

export function growthPageAfter(href: string): GrowthPage | null {
  const index = GROWTH_PAGES.findIndex((item) => item.href === href);
  if (index < 0 || index === GROWTH_PAGES.length - 1) return null;
  return GROWTH_PAGES[index + 1];
}

// Nothing freezes the call list on a schedule, so this does not promise Monday.
export const CALLS_EMPTY = "No one to call yet.";
export const CALLS_ALL_DONE = "You've reached everyone this week. Nice.";
export const FIRMS_EMPTY = "The firm list is empty. Pipeline Scout is filling it in.";
export const FIRMS_ALL_HAVE_PEOPLE = "Every top firm has someone we can call.";
export const REPLIES_EMPTY = "No replies yet. Most people answer within a few days.";
export const SENDS_EMPTY = "You haven't emailed anyone yet. Start from Who to call.";
export const REVIEW_OFF = "Weekly review is off. McFadyen turns it on.";
export const GOOGLE_NOT_CONNECTED = "Not connected yet. McFadyen needs to give access.";

export type FirmPerson = { id: string; name: string; onThisWeeksList: boolean };

/** Call card only when this week's frozen list includes the person. */
export function personStoryHref(person: { id: string; onThisWeeksList?: boolean }): string {
  if (person.onThisWeeksList) return `/admin/who-to-call#person-${person.id}`;
  return `/admin/contacts?contactId=${encodeURIComponent(person.id)}`;
}

/** Firm card only when Who we could sell to renders that firm. */
export function firmStoryHref(person: {
  id: string;
  firmId?: string | null;
  firmOnPage?: boolean;
  organizationId?: string | null;
}): string {
  if (person.firmId && person.firmOnPage) return `/admin/who-we-could-sell-to#firm-${person.firmId}`;
  if (person.organizationId) return `/admin/organizations/${encodeURIComponent(person.organizationId)}`;
  return `/admin/contacts?contactId=${encodeURIComponent(person.id)}`;
}

export type FirmWarmth = "warm" | "touched" | "cold";

export type FirmStoryRow = {
  id: string;
  name: string;
  top: boolean;
  warmth: FirmWarmth | null;
  contactCount: number;
  heardThisMonth: boolean;
  people: FirmPerson[];
};

/** Warm firms, then touched, then everyone else. Name only breaks a tie. */
export function firmListOrder<T extends { id: string; name: string; warmth: FirmWarmth | null }>(firms: readonly T[]): T[] {
  const rank = (warmth: FirmWarmth | null) => (warmth === "warm" ? 0 : warmth === "touched" ? 1 : 2);
  return [...firms].sort((left, right) =>
    rank(left.warmth) - rank(right.warmth)
    || left.name.localeCompare(right.name)
    || left.id.localeCompare(right.id));
}

export type CallStoryRow = {
  id: string;
  contactId: string | null;
  name: string;
  firm: string | null;
  whyNow: string;
  done: boolean;
};

const TOP_TIER = "Tier 1";
const DONE_OUTCOMES = new Set(["contacted", "replied", "meeting"]);

export function isTopFirm(tier: string | null | undefined): boolean {
  return (tier ?? "").trim() === TOP_TIER;
}

export function callIsDone(outcomes: readonly { type: string }[]): boolean {
  return outcomes.some((outcome) => DONE_OUTCOMES.has(outcome.type));
}

function etWeekday(now: Date): number {
  const name = new Intl.DateTimeFormat("en-US", {
    timeZone: "America/New_York",
    weekday: "short",
  }).format(now);
  const days: Record<string, number> = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };
  return days[name] ?? 0;
}

export function heardThisMonth(lastTouch: string | null, now: Date): boolean {
  const days = daysSince(lastTouch, now);
  return days != null && days >= 0 && days <= 30;
}

export function daysSince(iso: string | null, now: Date): number | null {
  if (!iso) return null;
  const then = Date.parse(iso);
  if (Number.isNaN(then)) return null;
  return Math.floor((now.getTime() - then) / 86_400_000);
}

export type WhySignal = {
  type: string | null;
  sourceUrl: string | null;
  at: string | null;
  note: string | null;
};

function weekdayShort(iso: string | null): string | null {
  if (!iso) return null;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  return new Intl.DateTimeFormat("en-US", {
    timeZone: "America/New_York",
    weekday: "short",
  }).format(date);
}

/** A sentence an admin can read. Raw signal names stay off the page. */
export function plainSignal(signal: WhySignal | null | undefined): string | null {
  if (!signal) return null;
  const note = signal.note?.trim() ?? "";
  if (/started a new job/i.test(note)) return "Started a new job";
  const type = (signal.type ?? "").trim().toLowerCase();
  if (type.includes("job")) return "Started a new job";
  const url = (signal.sourceUrl ?? "").trim().toLowerCase();
  const pricing = url.includes("pricing") || url.includes("/fees");
  const day = weekdayShort(signal.at);
  if (pricing) return day ? `Visited our pricing page ${day}` : "Visited our pricing page";
  if (type === "page_visit") return day ? `Visited our site ${day}` : "Visited our site";
  return null;
}

/** Plain reason. Never a score, a tier word, or a raw signal row. */
export function whyNow(input: {
  lastSignalAt: string | null;
  topFirm: boolean;
  now: Date;
  signal?: WhySignal | null;
}): string {
  const specific = plainSignal(input.signal);
  if (specific) return specific;
  const days = daysSince(input.lastSignalAt, input.now);
  if (input.topFirm) return "Works at one of our top firms.";
  if (days != null && days >= 0 && days <= 30) return "We heard about them this month";
  return "They are on this week's list";
}

/** Firm name for the grey line. A slug, or an empty value, stays hidden. */
export function firmGreyLine(...candidates: Array<string | null | undefined>): string | null {
  for (const candidate of candidates) {
    const text = candidate?.trim() ?? "";
    if (!text || looksLikeFirmSlug(text)) continue;
    return text;
  }
  return null;
}

function looksLikeFirmSlug(value: string): boolean {
  return !/\s/.test(value) && /[-_]/.test(value);
}

export function callsNeedNudge(done: number, total: number, now: Date): boolean {
  if (total <= 0) return false;
  if (etWeekday(now) < 3) return false;
  return done / total < GROWTH_CALLS_DONE_SHARE;
}

export function callNextLine(names: string[]): string {
  const seen = new Set<string>();
  const unique: string[] = [];
  for (const name of names) {
    const trimmed = name.trim();
    if (!trimmed) continue;
    const key = trimmed.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    unique.push(trimmed);
  }
  const shown = unique.slice(0, 3);
  if (shown.length === 0) return "Call the next person.";
  if (shown.length === 1) return `Call ${shown[0]}.`;
  if (shown.length === 2) return `Call ${shown[0]} and ${shown[1]}.`;
  return `Call ${shown[0]}, ${shown[1]}, and ${shown[2]}.`;
}

export function firmHeadline(top: number, withPerson: number, heard: number): string {
  const firms = top === 1 ? "1 top firm" : `${top} top firms`;
  const people = withPerson === 1 ? "1 has" : `${withPerson} have`;
  return `${firms} we want. ${people} a person we can call. ${heard} heard from us this month.`;
}

export function callHeadline(total: number, done: number): string {
  const people = total === 1 ? "1 person" : `${total} people`;
  return `${people} to call this week. ${done} done.`;
}

export function conversationHeadline(replies: number, meetings: number, waiting: number): string {
  const people = replies === 1 ? "1 person wrote back" : `${replies} people wrote back`;
  const meets = meetings === 1 ? "1 meeting" : `${meetings} meetings`;
  const wait = waiting === 1 ? "1 is waiting" : `${waiting} are waiting`;
  return `${people} this week, ${meets}. ${wait} on us.`;
}

export function replyIsLate(hoursWaiting: number): boolean {
  return hoursWaiting >= GROWTH_REPLY_WAIT_HOURS;
}

export function thisWeekHeadline(conversations: number, meetings: number): string {
  const talks = conversations === 1 ? "1 new conversation" : `${conversations} new conversations`;
  const meets = meetings === 1 ? "1 meeting" : `${meetings} meetings`;
  return `This week: ${talks}, ${meets}. Here's who to call.`;
}

export function funnelSentence(reached: number, replies: number, meetings: number): string {
  const back = replies === 1 ? "1 wrote back" : `${replies} wrote back`;
  const booked = meetings === 1 ? "1 booked a meeting" : `${meetings} booked meetings`;
  return `We reached ${reached} people, ${back}, ${booked}.`;
}

/** True when this week is more than a quarter below last week. */
export function weekDropped(current: number, prior: number): boolean {
  if (prior <= 0 || current >= prior) return false;
  return (prior - current) / prior > GROWTH_WEEK_DROP_SHARE;
}

export function googleHeadline(shown: number, total: number, missing: number): string {
  const pages = missing === 1 ? "1 important page is" : `${missing} important pages are`;
  return `Google shows ${shown} of our ${total} pages. ${pages} missing.`;
}

export function pageDropMatters(previousShown: number, shown: number): boolean {
  return previousShown - shown >= GROWTH_PAGE_DROP_COUNT;
}

export function plainWhen(iso: string | null): string {
  if (!iso) return "Not refreshed yet";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "Not refreshed yet";
  return new Intl.DateTimeFormat("en-US", {
    timeZone: "America/New_York",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(date);
}

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/** A YYYY-MM-DD week key, shown as that calendar date. */
export function plainWeekDate(iso: string | null): string {
  if (!iso) return "Not refreshed yet";
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso.trim());
  if (!match) return plainWhen(iso);
  const month = Number(match[2]);
  const day = Number(match[3]);
  if (month < 1 || month > 12 || day < 1 || day > 31) return "Not refreshed yet";
  return `${MONTHS[month - 1]} ${day}`;
}

const WEEKDAY: Record<string, number> = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };

/** Monday 00:00 America/New_York, as a UTC instant. */
export function etWeekStart(now: Date): Date {
  const bag = Object.fromEntries(
    new Intl.DateTimeFormat("en-US", {
      timeZone: "America/New_York",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      weekday: "short",
    }).formatToParts(now).map((part) => [part.type, part.value]),
  );
  const weekday = WEEKDAY[bag.weekday] ?? 1;
  const sinceMonday = weekday === 0 ? 6 : weekday - 1;
  const noon = new Date(Date.UTC(
    Number(bag.year),
    Number(bag.month) - 1,
    Number(bag.day) - sinceMonday,
    12,
  ));
  const clock = Object.fromEntries(
    new Intl.DateTimeFormat("en-US", {
      timeZone: "America/New_York",
      hour: "2-digit",
      minute: "2-digit",
      hourCycle: "h23",
    }).formatToParts(noon).map((part) => [part.type, part.value]),
  );
  return new Date(noon.getTime() - (Number(clock.hour) * 60 + Number(clock.minute)) * 60_000);
}

export type ConversationKind = "reply" | "meeting" | "send";

export type ConversationEvent = {
  contactId: string;
  name: string;
  firm: string | null;
  firmId: string | null;
  organizationId?: string | null;
  onThisWeeksList?: boolean;
  firmOnPage?: boolean;
  kind: ConversationKind;
  at: string;
};

export type WaitingPerson = {
  id: string;
  name: string;
  firm: string | null;
  firmId: string | null;
  organizationId: string | null;
  onThisWeeksList: boolean;
  firmOnPage: boolean;
  since: string;
  late: boolean;
};

export type ConversationStory = {
  replies: number;
  meetings: number;
  sendCount: number;
  waiting: WaitingPerson[];
};

function eventTime(iso: string): number | null {
  const time = Date.parse(iso);
  return Number.isNaN(time) ? null : time;
}

export function buildConversationStory(events: readonly ConversationEvent[], now: Date): ConversationStory {
  const start = etWeekStart(now).getTime();
  const nowMs = now.getTime();
  const byContact = new Map<string, ConversationEvent[]>();
  for (const event of events) {
    const list = byContact.get(event.contactId) ?? [];
    list.push(event);
    byContact.set(event.contactId, list);
  }
  let replies = 0;
  let meetings = 0;
  let sendCount = 0;
  const waiting: WaitingPerson[] = [];
  for (const [id, list] of byContact) {
    const name = list.find((event) => event.name.trim())?.name ?? "Someone";
    const firm = list.find((event) => event.firm)?.firm ?? null;
    const firmId = list.find((event) => event.firmId)?.firmId ?? null;
    const organizationId = list.find((event) => event.organizationId)?.organizationId ?? null;
    const onThisWeeksList = list.some((event) => event.onThisWeeksList === true);
    const firmOnPage = list.some((event) => event.firmOnPage === true);
    const times = (kind: ConversationKind) => list
      .filter((event) => event.kind === kind)
      .map((event) => eventTime(event.at))
      .filter((time): time is number => time != null);
    const replyTimes = times("reply");
    const meetingTimes = times("meeting");
    const sendTimes = times("send");
    sendCount += sendTimes.length;
    if (replyTimes.some((time) => time >= start && time <= nowMs)) replies += 1;
    if (meetingTimes.some((time) => time >= start && time <= nowMs)) meetings += 1;
    const lastReply = replyTimes.length > 0 ? Math.max(...replyTimes) : null;
    const lastSend = sendTimes.length > 0 ? Math.max(...sendTimes) : null;
    if (lastReply != null && (lastSend == null || lastReply > lastSend)) {
      waiting.push({
        id,
        name,
        firm,
        firmId,
        organizationId,
        onThisWeeksList,
        firmOnPage,
        since: new Date(lastReply).toISOString(),
        late: replyIsLate((nowMs - lastReply) / 3_600_000),
      });
    }
  }
  waiting.sort((a, b) => a.since.localeCompare(b.since) || a.name.localeCompare(b.name));
  return { replies, meetings, sendCount, waiting };
}

export function conversationEmpty(story: ConversationStory): string | null {
  if (story.replies > 0 || story.meetings > 0 || story.waiting.length > 0) return null;
  return story.sendCount === 0 ? SENDS_EMPTY : REPLIES_EMPTY;
}

export type WeekCount = {
  stageKey: string;
  weekStart: string;
  count: number;
};

export function weekCounts(rows: readonly WeekCount[], stageKey: string): { current: number; prior: number } {
  const mine = rows
    .filter((row) => row.stageKey === stageKey)
    .sort((a, b) => b.weekStart.localeCompare(a.weekStart));
  return { current: mine[0]?.count ?? 0, prior: mine[1]?.count ?? 0 };
}

export type FunnelStep = {
  label: string;
  href: string;
  current: number;
  prior: number;
};

export function weakestStep(steps: readonly FunnelStep[]): FunnelStep | null {
  if (steps.length === 0) return null;
  const dropped = steps.filter((step) => weekDropped(step.current, step.prior));
  const pool = dropped.length > 0 ? dropped : [...steps];
  return [...pool].sort((a, b) => a.current - b.current || a.label.localeCompare(b.label))[0] ?? null;
}

export function weekChangeSentence(steps: readonly FunnelStep[]): string {
  const moved = steps
    .map((step) => ({ ...step, delta: step.current - step.prior }))
    .filter((step) => step.delta !== 0)
    .sort((a, b) => Math.abs(b.delta) - Math.abs(a.delta));
  const top = moved[0];
  if (!top) return "Nothing moved from last week.";
  return top.delta < 0 ? `${top.label} got worse.` : `${top.label} got better.`;
}

export function plainBet(raw: string): string {
  const body = raw
    .replace(/^Proposed bet \d+ \(proposal, not a decision\): /, "")
    .split(" Figure:")[0]
    ?.trim() ?? "";
  if (!body || /[A-Z][A-Z0-9]+_[A-Z0-9_]+/.test(body) || /flag/i.test(body)) {
    return "We do not have enough to judge that yet.";
  }
  return body;
}

export type GooglePage = {
  title: string;
  path: string;
  important: boolean;
  shown: boolean | null;
};

export function googleStory(pages: readonly GooglePage[]): {
  connected: boolean;
  shown: number;
  total: number;
  missing: GooglePage[];
} {
  const connected = pages.some((page) => page.shown !== null);
  const shown = pages.filter((page) => page.shown === true).length;
  const missing = pages.filter((page) => {
    if (!page.important) return false;
    if (page.shown === false) return true;
    return connected && page.shown === null;
  });
  return { connected, shown, total: pages.length, missing };
}


